import React from 'react'
import isEmpty from 'lodash/isEmpty'
import { BusyButton } from '@ztechapps/webui.button'
import { Alert, ShowIf } from '@ztechapps/webui.util'
import { formatMoney, getWindow } from '@ztechapps/common.utils'
import { useContext as useOrderContext } from '../Order/Context'
import { useOrderSignInGuard } from '../../hooks/useOrderSignInGuard'
import { usePayBalance } from './usePayBalance'

// $0.50 Stripe card minimum — mirrors useStartCardPayment.ts:52-54
const STRIPE_CARD_MINIMUM = 0.5

export type PayBalanceButtonProps = {
  accessToken?: string
}

/**
 * Pay-balance button for post-shipment unpaid/partially-paid orders: charges the
 * outstanding balance via Stripe hosted checkout directly, skipping the cart wizard
 * entirely. Hidden pre-shipment (through Packing, where the sibling Checkout button
 * still shows) and once the order is fully paid — its status set is disjoint from the
 * sibling Checkout button's (InvoiceCheckoutButton on the invoice page, GoToCartButton
 * on My Orders), so at most one of the two ever renders.
 */
export const PayBalanceButton = ({ accessToken }: PayBalanceButtonProps = {}) => {
  const { isPostShipmentPayable } = useOrderContext()
  const { startPayment, isBusy, error, balance } = usePayBalance({ accessToken })
  const { guard } = useOrderSignInGuard({ redirectUrl: getWindow()?.location?.href })

  const onClick = () => guard(startPayment)

  return (
    <ShowIf condition={isPostShipmentPayable && balance > STRIPE_CARD_MINIMUM}>
      <>
        <BusyButton icon="credit-card" text={`Pay Balance ${formatMoney(balance)}`} isBusy={isBusy} onClick={onClick} />
        <ShowIf condition={!isEmpty(error)}>
          <Alert type="danger">{error}</Alert>
        </ShowIf>
      </>
    </ShowIf>
  )
}
